import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowRight, ArrowUpRight, Menu, X, MapPin } from "lucide-react";
import { SERVICES } from "@/data/services";

export const VoltLogo = ({ className = "" }) => (
  <Link to="/" data-testid="logo-home-link" className={`flex items-center gap-2 ${className}`}>
    <svg viewBox="0 0 64 64" className="h-8 w-8" aria-hidden="true">
      <defs>
        <linearGradient id="logoG" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#00EAFF" />
          <stop offset=".5" stopColor="#38BDF8" />
          <stop offset="1" stopColor="#1F5CFF" />
        </linearGradient>
      </defs>
      <path fill="url(#logoG)" d="M10 14h13l9 22 9-22h13L37 52H27L10 14z" />
      <path fill="#00EAFF" d="M40 12l8 2-7 12h8l-14 18 3-12h-8l10-20z" />
    </svg>
    <span className="font-display text-xl font-extrabold tracking-tight text-slate-50">
      olt<span className="text-volt-gradient">.</span>
      <span className="ml-1.5 hidden font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-slate-500 sm:inline">software</span>
    </span>
  </Link>
);

const navLinkClass = ({ isActive }) =>
  `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-cyan-300" : "text-slate-400 hover:text-cyan-300"
  }`;

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const goAnchor = (hash) => (e) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate(`/${hash}`);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-all duration-300 ${
        scrolled || menuOpen || mobileOpen
          ? "border-cyan-500/15 bg-[#FFFFFF]/90"
          : "border-cyan-500/5 bg-[#FFFFFF]/70"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-10">
        <VoltLogo />

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          <div className="relative" onMouseEnter={() => setMenuOpen(true)} onMouseLeave={() => setMenuOpen(false)}>
            <button
              data-testid="nav-services-dropdown"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                menuOpen || location.pathname.startsWith("/services")
                  ? "text-cyan-300"
                  : "text-slate-400 hover:text-cyan-300"
              }`}
            >
              Services
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${menuOpen ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  data-testid="mega-menu"
                  initial={{ opacity: 0, y: 12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute left-1/2 top-full w-[880px] max-w-[92vw] -translate-x-1/2 pt-4"
                >
                  <div className="glass-panel overflow-hidden rounded-2xl p-3 shadow-[0_24px_80px_rgba(15,23,42,0.16)]">
                    <div className="grid grid-cols-3 gap-1">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          data-testid={`mega-menu-${s.slug}`}
                          className="group rounded-xl p-4 transition-colors duration-200 hover:bg-cyan-950/40"
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-400/20 bg-cyan-400/5 text-cyan-400 transition-colors duration-200 group-hover:border-cyan-400/50 group-hover:text-cyan-300">
                              <s.icon className="h-4.5 w-4.5" strokeWidth={1.8} />
                            </span>
                            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600 group-hover:text-cyan-500/70">
                              {s.badge}
                            </span>
                          </div>
                          <p className="font-display text-sm font-semibold text-slate-100 transition-colors duration-200 group-hover:text-cyan-300">
                            {s.title}
                          </p>
                          <p className="mt-1 text-xs leading-relaxed text-slate-500">{s.short}</p>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center justify-between rounded-xl border border-cyan-500/10 bg-[#F1F5F9] px-5 py-3.5">
                      <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                        <MapPin className="h-3.5 w-3.5 text-cyan-500" /> Lincolnton, NC — deploying worldwide
                      </span>
                      <Link
                        to="/services"
                        data-testid="mega-menu-all-services"
                        className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-400 transition-colors hover:text-cyan-300"
                      >
                        All services <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/#process" data-testid="nav-process" onClick={goAnchor("#process")} className="rounded-full px-4 py-2 text-sm font-medium text-slate-400 transition-colors duration-200 hover:text-cyan-300">
            Process
          </Link>
          <NavLink to="/insights" data-testid="nav-insights" className={navLinkClass}>
            Insights
          </NavLink>
          <Link to="/#about" data-testid="nav-about" onClick={goAnchor("#about")} className="rounded-full px-4 py-2 text-sm font-medium text-slate-400 transition-colors duration-200 hover:text-cyan-300">
            About
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="hidden items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-600 px-5 py-2.5 font-display text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(56,189,248,0.45)] sm:inline-flex"
          >
            Start a Project <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-cyan-500/20 text-slate-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-300 lg:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-cyan-500/10 bg-[#FFFFFF]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="max-h-[70vh] overflow-y-auto px-5 py-6">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-cyan-500">Services</p>
              <div className="grid gap-1">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`mobile-nav-${s.slug}`}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-300 transition-colors hover:bg-cyan-950/40 hover:text-cyan-300"
                  >
                    <s.icon className="h-4 w-4 text-cyan-500" strokeWidth={1.8} />
                    {s.title}
                  </Link>
                ))}
              </div>
              <div className="mt-5 grid gap-1 border-t border-cyan-500/10 pt-5">
                <Link to="/#process" onClick={goAnchor("#process")} data-testid="mobile-nav-process" className="rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:text-cyan-300">Process</Link>
                <Link to="/insights" data-testid="mobile-nav-insights" className="rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:text-cyan-300">Insights</Link>
                <Link to="/#about" onClick={goAnchor("#about")} data-testid="mobile-nav-about" className="rounded-lg px-3 py-2.5 text-sm text-slate-300 hover:text-cyan-300">About</Link>
                <Link
                  to="/contact"
                  data-testid="mobile-nav-contact"
                  className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-600 px-5 py-3 font-display text-sm font-semibold text-white"
                >
                  Start a Project <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
